import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./apiClient";

const SAVE_EVERY_MS = 15000;
const GUEST_SPOT = "qtv_guest_spot";

export const rememberGuestSpot = (youtubeId, position) => {
  sessionStorage.setItem(GUEST_SPOT, JSON.stringify({ youtubeId, position: Math.floor(position) }));
};

const takeGuestSpot = (youtubeId) => {
  try {
    const spot = JSON.parse(sessionStorage.getItem(GUEST_SPOT) || "null");
    if (spot?.youtubeId !== youtubeId) return 0;
    sessionStorage.removeItem(GUEST_SPOT);
    return spot.position || 0;
  } catch {
    return 0;
  }
};

// Loads the saved resume point for a video and saves playback progress for signed-in viewers.
export const useWatchProgress = (video, user) => {
  const [start, setStart] = useState(null);
  const latest = useRef(null);
  const lastSave = useRef(0);
  const videoId = video?.id;
  const userId = user?.id;
  const ytId = video?.youtube_id;

  const save = useCallback((position, duration) => {
    if (!userId || !ytId || !duration) return;
    lastSave.current = Date.now();
    api.post("/watch/progress", { video_id: videoId, youtube_id: ytId, position, duration }).catch(() => {});
  }, [userId, videoId, ytId]);

  useEffect(() => {
    setStart(null);
    latest.current = null;
    lastSave.current = 0;
    if (!ytId) return;
    if (!userId) {
      setStart(0);
      return;
    }
    let cancelled = false;
    api.get(`/watch/progress/${ytId}`)
      .then((r) => !cancelled && setStart(Math.floor(r.data.position || 0) || takeGuestSpot(ytId)))
      .catch(() => !cancelled && setStart(0));
    return () => {
      cancelled = true;
      if (latest.current) save(latest.current.position, latest.current.duration);
    };
  }, [ytId, userId, save]);

  const onTime = useCallback((position, duration) => {
    latest.current = { position, duration };
    if (Date.now() - lastSave.current > SAVE_EVERY_MS) save(position, duration);
  }, [save]);

  const onEnded = useCallback(() => {
    if (latest.current) save(latest.current.duration, latest.current.duration);
    latest.current = null;
  }, [save]);

  return { ready: start !== null, start: start || 0, onTime, onEnded };
};
